import { useState } from "react"
import { useTranslation } from "@/clientWeb/i18n/LanguageContext"
import { Search, Users } from "lucide-react"
import { AvatarPhoto } from "./avatar-photo"

interface Group {
  id: string
  name: string
  subtitle: string
  time: string
  imageUrl: string
  unreadCount?: number
}

interface GroupsTabProps {
  onBack?: () => void
  onSelectGroup?: (group: Group) => void
}

const MOCK_GROUPS: Group[] = [
  {
    id: "g1",
    name: "Project Alpha",
    subtitle: "Michael: Final build is ready",
    time: "01:12 PM",
    imageUrl: "https://picsum.photos/200/200?random=301",
  },
  {
    id: "g2",
    name: "Family Group",
    subtitle: "Emma: Dinner at 8?",
    time: "12:45 PM",
    imageUrl: "https://picsum.photos/200/200?random=302",
  },
  {
    id: "g3",
    name: "Design Team",
    subtitle: "Sarah: Updated Figma link",
    time: "11:20 AM",
    imageUrl: "https://picsum.photos/200/200?random=303",
  },
  {
    id: "g4",
    name: "College Friends",
    subtitle: "Alex: Trip plan finalized",
    time: "Yesterday",
    imageUrl: "https://picsum.photos/200/200?random=304",
  },
  {
    id: "g5",
    name: "Weekend Football",
    subtitle: "Kevin: Ground booked",
    time: "Dec 6",
    imageUrl: "https://picsum.photos/200/200?random=305",
  },
  {
    id: "g6",
    name: "QA Squad",
    subtitle: "David: Regression done",
    time: "Dec 5",
    imageUrl: "https://picsum.photos/200/200?random=306",
  },
  {
    id: "g7",
    name: "Event Planning",
    subtitle: "Emma: Venue options shared",
    time: "Dec 4",
    imageUrl: "https://picsum.photos/200/200?random=307",
  },
  {
    id: "g8",
    name: "Marketing Team",
    subtitle: "Sarah: Campaign draft",
    time: "Dec 3",
    imageUrl: "https://picsum.photos/200/200?random=308",
  },
]

export function GroupsTab({ onBack, onSelectGroup }: GroupsTabProps) {
  const t = useTranslation()
  const [searchQuery, setSearchQuery] = useState("")
  const [isSearching, setIsSearching] = useState(false)

  const filteredGroups = MOCK_GROUPS.filter(group =>
    group.name.toLowerCase().includes(searchQuery.toLowerCase())
  )

  return (
    <div className="flex flex-col h-full bg-card overflow-hidden">
      {/* Header */}
      <div className="flex items-center gap-2 px-4 py-3 border-b border-border shrink-0">
        {onBack && (
          <button
            onClick={onBack}
            className="p-2 rounded-full hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
        )}
        {isSearching ? (
          <div className="flex items-center gap-2 flex-1">
            <input
              autoFocus
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder={t("presence.searchGroupsPlaceholder")}
              className="flex-1 bg-transparent text-sm text-foreground placeholder:text-muted-foreground outline-none"
            />
            <button
              onClick={() => { setIsSearching(false); setSearchQuery("") }}
              className="p-1.5 rounded-full hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        ) : (
          <>
            <h2 className="text-base font-semibold text-foreground">{t("presence.groups")}</h2>
            <div className="flex items-center gap-1 ml-auto">
              <button
                onClick={() => setIsSearching(true)}
                className="p-2 rounded-full hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                title="Search"
              >
                <Search className="w-4.5 h-4.5" />
              </button>
            </div>
          </>
        )}
      </div>

      {/* Group list */}
      <div className="flex-1 overflow-y-auto">
        {filteredGroups.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center px-8 py-16">
            <Users className="w-12 h-12 text-muted-foreground/30 mb-4" />
            <p className="text-sm text-muted-foreground">{t("presence.noGroupsFound")}</p>
          </div>
        ) : (
          filteredGroups.map(group => (
            <div
              key={group.id}
              onClick={() => onSelectGroup?.(group)}
              className="flex items-center gap-3 px-4 py-3 hover:bg-muted/50 cursor-pointer transition-colors"
            >
              <div className="w-12 h-12 rounded-full bg-muted overflow-hidden shrink-0">
                <AvatarPhoto
                  src={group.imageUrl}
                  alt={group.name}
                  className="w-full h-full object-cover"
                  fallback={
                    <div className="w-full h-full flex items-center justify-center text-sm font-semibold text-muted-foreground bg-primary/10 text-primary">
                      {group.name[0]?.toUpperCase() || "?"}
                    </div>
                  }
                />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-foreground truncate">{group.name}</p>
                <p className="text-xs text-muted-foreground truncate">{group.subtitle}</p>
              </div>
              <span className="text-xs text-muted-foreground shrink-0">{group.time}</span>
            </div>
          ))
        )}
      </div>
    </div>
  )
}
